import { getProductPrices } from '@gn-audio/jss-core';

import {
  FamilyGroup,
  getAttributesByGroups,
  getGroupDetails,
  SegmentTypes,
} from 'api/productCatalogueApi';
import { fetchProductCatalogSearchResults } from 'api/productCatalogueApi/fetchProductCatalogSearchResults';
import {
  SearchItem,
  SearchParameters,
  SearchUrlSuffix,
} from 'api/productCatalogueApi/productCatalogueApiTypes';
import { fetchSfccSearchResults } from 'api/sfccApi/fetchSfccSearchResults';
import { ISfccSearchRefinement } from 'api/sfccApi/sfccApiTypes';
import { ProductEntityType } from 'common/types/productTypes';
import { PIMProduct } from 'components/common/MagicCards/MagicCards';

import filterMapData from '../productFilterMap.json';
import {
  CombinedResults,
  FetchProductDetailsProps,
  ISFCCGroupPrice,
  ProductFilterGroup,
  ProductFilterModule,
  SelectedProductFilters,
} from '../ProductFilterModuleTypes';

const filterMap: Map<string, string> = new Map(Object.entries(filterMapData));

export const fetchSfccProductDetails = async ({
  sfccApiSettings,
  sfccSearchQuery,
  locale,
  productCatalogueApi,
}: FetchProductDetailsProps): Promise<{
  refinements: ISfccSearchRefinement[];
  combinedResults: CombinedResults[];
}> => {
  try {
    const sfccSearchResult = await fetchSfccSearchResults(sfccApiSettings, sfccSearchQuery);

    if (!sfccSearchResult?.total) return { refinements: [], combinedResults: [] };

    const refinements = sfccSearchResult.refinements || [];
    const sfccSearchResultSkus = sfccSearchResult.hits.map((hit) => hit.product_id) || [];

    const sfccPricesData = (await getProductPrices(sfccSearchResultSkus)) as unknown as
      | ISFCCGroupPrice[]
      | null;
    if (!sfccPricesData) return { refinements, combinedResults: [] };

    const productCatalogueGroupIds =
      sfccPricesData.map((data: ISFCCGroupPrice) => String(data.c_pimGroupId)) || [];

    const productCatalogueLabels = await getAttributesByGroups(
      productCatalogueGroupIds,
      locale,
      productCatalogueApi
    );

    if (productCatalogueLabels) {
      const productCatalogueDetailsPromises = productCatalogueGroupIds.map((id) =>
        getGroupDetails(id, locale, productCatalogueApi)
      );

      const productCatalogueDetails = await Promise.all(productCatalogueDetailsPromises);

      if (productCatalogueDetails) {
        const combinedResults = sfccPricesData.map((priceData) => {
          const matchingLabel = productCatalogueLabels.find(
            (label: FamilyGroup) => String(label.groupId) === priceData.c_pimGroupId
          );
          const matchingDetail = productCatalogueDetails.find(
            (detail: PIMProduct) => String(detail.groupId) === priceData.c_pimGroupId
          );

          return {
            sfccPricesData: priceData,
            productCatalogueLabels: matchingLabel,
            productCatalogueDetails: matchingDetail,
          };
        });

        return { refinements, combinedResults };
      }
    }
  } catch (error) {
    console.error('An error occurred:', error);
  }

  return { refinements: [], combinedResults: [] };
};

export const fetchCatalogProductDetails = async (
  parameters: SearchParameters,
  entityType: ProductEntityType
): Promise<{
  refinements: ISfccSearchRefinement[];
  combinedResults: CombinedResults[];
}> => {
  const catalogSearchResult = await (entityType === ProductEntityType.Family
    ? fetchProductCatalogSearchResults(SearchUrlSuffix.Families, parameters)
    : fetchProductCatalogSearchResults(SearchUrlSuffix.Groups, parameters));

  if (!catalogSearchResult?.count) {
    return { refinements: [], combinedResults: [] };
  }

  const mapImages = (images?: string[]) => images?.map((url) => ({ url })) || [];

  const mapAttributes = (attributes?: string[]) =>
    attributes?.map((attribute) => {
      const [key, value] = attribute.split('|');
      return { key, value, valueKey: key };
    }) || [];

  const mapItemToCombinedResults = (item: SearchItem): CombinedResults => {
    const {
      familyId,
      familyName = '',
      groupId,
      pageUrl = '',
      sku = '',
      title = '',
      groupState,
      familyState,
      supportPageUrl = '',
      hasBluetoothPairingGuide = false,
      segmentType,
      images,
      attributes,
      summary = '',
      uniqueSellingPoint = '',
    } = item;

    return {
      productCatalogueLabels: {
        familyId,
        familyName,
        groupId,
        pageUrl,
        sku,
        productName: title,
        name: title,
        groupState: groupState || familyState || 0,
        supportPageUrl,
        hasBluetoothPairingGuide,
        segmentType: segmentType ?? SegmentTypes.Business,
        images: mapImages(images),
        attributes: mapAttributes(attributes),
      },
      productCatalogueDetails: {
        title,
        familyId,
        groupId: groupId.toString(),
        productName: title,
        name: title,
        summary,
        uniqueSellingPoint,
        hasBluetoothPairingGuide,
        pageUrl,
        supportPageUrl,
        description: '',
        thumbnailUrl: images?.[0] || '',
        segmentType: segmentType?.toString() || '',
      },
    };
  };

  const combinedResults = catalogSearchResult.items.map(mapItemToCombinedResults);

  return { refinements: [], combinedResults };
};

export const buildProductODatagQuery = (
  groups: ProductFilterGroup[] = [],
  preFilter = ''
): string => {
  const filterQueries = groups.flatMap((group: ProductFilterGroup) => {
    const newValues = group.AttachedFilters.flatMap(
      (filter) => `${group.AttributeName.value}|${filter.Value.value}`
    )
      .filter(Boolean)
      .join(',');

    return newValues ? `attributes/any(a: search.in(a, '${newValues}'))` : '';
  });

  const query = filterQueries.length ? `(${filterQueries.join(' and ')})` : '';

  return [preFilter, query].filter(Boolean).join(' and ');
};

export const buildSfccSearchQuery = (
  searchParameters: SelectedProductFilters,
  sfccLocale: string,
  preFilter?: string,
  take: number = 25,
  skip: number = 0
): string => {
  const BASE_FILTER_QUERY = `refine_1=c_countryProductState=3&expand=prices&locale=${sfccLocale}`;
  const MAX_REFINE_COUNT = 9;
  let refineCount = 2;

  const filterValues: Map<string, string[]> = new Map();

  // Handle search parameters
  searchParameters.FilterGroups.forEach((filter) => {
    const filterName = filterMap.get(filter.AttributeName.value);
    if (filterName) {
      const existingValues = filterValues.get(filterName);
      const newValues = filter.AttachedFilters.flatMap((filter) => filter.Value.value);
      if (existingValues) {
        existingValues.push(...newValues);
      } else {
        filterValues.set(filterName, [...newValues]);
      }
    }
  });

  // Handle preFilter and merge with search parameters
  if (preFilter) {
    const preFilterValues = transformODataToSFCC(preFilter);

    preFilterValues.forEach((values, filterName) => {
      const existingValues = filterValues.get(filterName) || [];
      values.forEach((value) => {
        if (!existingValues.includes(value)) {
          existingValues.push(value);
        }
      });
      filterValues.set(filterName, existingValues);
    });
  }

  let filterQuery = filterValues.size ? BASE_FILTER_QUERY : '';

  filterValues.forEach((values, filterName) => {
    if (refineCount <= MAX_REFINE_COUNT) {
      filterQuery += `&refine_${refineCount}=${filterName}=${values.join('|')}`;
      refineCount++;
    }
  });

  if (take) {
    filterQuery += `&count=${take}`;
  }

  if (skip) {
    filterQuery += `&start=${skip}`;
  }

  if (searchParameters.SearchText) {
    filterQuery += `&q=${searchParameters.SearchText}`;
  }

  if (searchParameters.Order) {
    const sortValue = filterMap.get(searchParameters.Order.value);
    if (sortValue) {
      filterQuery += `&sort=${sortValue}`;
    }
  }

  if (searchParameters.PriceRange) {
    if (refineCount <= MAX_REFINE_COUNT) {
      const priceRangeFilter = searchParameters.PriceRange;
      const minPrice = priceRangeFilter.min;
      const maxPrice = priceRangeFilter.max;
      filterQuery += `&refine_${refineCount}=price=(${minPrice}..${maxPrice})`;
      refineCount++;
    }
  }
  if (searchParameters.SelectedColor.length) {
    if (refineCount <= MAX_REFINE_COUNT) {
      const colorFilter = searchParameters.SelectedColor;
      const colorValue = colorFilter.map((color) => color.value).join('|');
      filterQuery += `&refine_${refineCount}=color=${colorValue}`;
    }
    refineCount++;
  }

  return `${filterQuery}`;
};

export const buildUrlQueryParams = (filters: SelectedProductFilters): string => {
  const queryParams = new URLSearchParams();

  // Append Search Text
  if (filters.SearchText) {
    queryParams.append('search', filters.SearchText);
  }

  // Append Selected Filters by Group Title
  filters.FilterGroups.forEach((group) => {
    const selectedFilterTitles = group.AttachedFilters.map((filter) => filter.Value.value).join(
      '|'
    );

    queryParams.append(group.AttributeName.value, selectedFilterTitles);
  });

  // Append Order if specified
  if (filters.Order) {
    queryParams.append('order', filters.Order.value);
  }

  if (filters.PriceRange) {
    queryParams.append('price', `(${filters.PriceRange.min}..${filters.PriceRange.max})`);
  }

  if (filters.SelectedColor.length) {
    const selectedColors = filters.SelectedColor.map(
      (color) => `${color.title}-${color.value}`
    ).join('|');
    queryParams.append('color', selectedColors);
  }

  return queryParams.toString();
};

export const parseUrlQueryParams = (
  queryString: string,
  allFilters: ProductFilterModule
): SelectedProductFilters => {
  const query = queryString.split('?')[1] || '';
  const queryParams = new URLSearchParams(query);
  const selectedFilters: SelectedProductFilters = {
    FilterGroups: [],
    SelectedColor: [],
  };

  // Parse Search Text
  selectedFilters.SearchText = queryParams.get('search') ?? '';

  // Parse Price Range
  const priceRange = queryParams.get('price');
  if (priceRange) {
    selectedFilters.PriceRange = formatSfccPriceRange(priceRange);
  }

  // Parse Selected Color
  const selectedColor = queryParams.get('color');
  if (selectedColor) {
    const color = selectedColor.split('|');
    color.forEach((color) => {
      const [colorTitle, colorValue] = color.split('-');
      selectedFilters.SelectedColor?.push({
        key: colorValue,
        title: colorTitle,
        value: colorValue,
      });
    });
  }

  // Parse Filter Groups
  allFilters.FilterGroups.forEach((group) => {
    const selectedFilterTitles = queryParams.get(group.AttributeName.value);
    if (selectedFilterTitles) {
      const selectedTitlesArray = selectedFilterTitles.split('|');
      const selectedAttachedFilters = group.AttachedFilters.filter((filter) =>
        selectedTitlesArray.includes(filter.Value.value)
      );
      if (selectedAttachedFilters.length > 0) {
        selectedFilters.FilterGroups.push({
          ...group,
          AttachedFilters: selectedAttachedFilters,
        });
      }
    }
  });

  // Parse Order
  const order = queryParams.get('order');
  if (order) {
    const [key, direction] = order.split(' ');
    const orderOption = allFilters.Orders.find(
      (order) => order.Property.value === key && order.Direction.value === direction
    );
    if (orderOption) {
      selectedFilters.Order = {
        key: orderOption.Property.value,
        title: orderOption.Title.value,
        value: `${orderOption.Property.value} ${orderOption.Direction.value}`,
      };
    }
  }

  return selectedFilters;
};

const transformODataToSFCC = (oDataQuery: string): Map<string, string[]> => {
  const filterValues: Map<string, string[]> = new Map();

  const attributeConditions = oDataQuery.match(/attributes\/any\(a: a eq '([^']+)'\)/g);

  if (attributeConditions) {
    attributeConditions.forEach((condition) => {
      const [, attributeValue] = condition.match(/attributes\/any\(a: a eq '([^']+)'\)/) || [];
      const [filterName, value] = attributeValue.split('|');
      const mappedFilterName = filterMap.get(filterName);

      if (mappedFilterName) {
        const existingValues = filterValues.get(mappedFilterName) || [];
        existingValues.push(value);
        filterValues.set(mappedFilterName, existingValues);
      }
    });
  }

  return filterValues;
};

export const getRefinementValueByAttributeId = (
  attributeId: string,
  searchRefinements: ISfccSearchRefinement[]
) => {
  return searchRefinements.find(
    (refinement) => refinement.attribute_id === attributeId && refinement.values
  );
};

export const formatSfccPriceRange = (
  priceValues: string | undefined
): { min: number; max: number } => {
  const priceRange = priceValues?.match(/\d+/g)?.map(Number);
  if (!priceRange || priceRange.length < 2) {
    return { min: 0, max: 1500 };
  }
  let [min, max] = priceRange;

  min = Math.floor(min / 10) * 10;
  max = Math.ceil(max / 10) * 10;

  return { min, max };
};
